<template>
  <div class="context-tab" :class="`context-tab--${state}`">
    <!-- Uploaded reference files (self-manages empty / one-row / two-row layout from `files`). -->
    <ContextFiles
      :files="files"
      :max-files="maxFiles"
      :disabled="disabled"
      :uploading-files="uploadingFiles"
      :openable="true"
      @upload="emit('upload', $event)"
      @remove="emit('remove', $event)"
      @open-file="openFile"
    />

    <!-- Free-text Context field (self-manages empty / dirty / filled / over-limit from `context` + edits). -->
    <ContextEditor
      :title="title"
      :context="context"
      :draft="draft"
      :max-length="maxLength"
      :disabled="disabled"
      :suggesting="suggesting"
      :suggest-error="suggestError"
      :suggest-hint="suggestHint"
      @save="emit('save', $event)"
      @discard="emit('discard')"
      @suggest="emit('suggest')"
      @improve="emit('improve')"
      @input="emit('input', $event)"
    />

    <!--
      ER-11428: a single click on a file card opens the read-only reader modal
      (reverses ER-11347's double-click). The reader is pure UI (preview/fallback);
      no parent state is involved.
    -->
    <FileReaderModal
      :value="isReaderOpen"
      :file="selectedFile"
      @input="closeReader"
      @close="closeReader"
    />

    <!--
      Filled-state history: the Figma frame renders a second, read-only ContextFiles + ContextEditor
      pair beneath the live one, reading as a log of a prior saved context. Its data shape and behavior
      (always shown vs only when a prior version exists, remove/download actions) are still TBD and must
      be confirmed with design/PM before building — see ER-10314. Deferred intentionally; not built here.
    -->
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import ContextFiles from "@components/ContextFiles/ContextFiles.vue";
import ContextEditor from "@components/ContextEditor/ContextEditor.vue";
import FileReaderModal from "@components/FileReaderModal/FileReaderModal.vue";

defineProps({
  // Which Figma variant renders. Stable keys mapped from the Figma display values Empty/Dirty/Filled.
  // The nested components derive their own visuals from `files` and `context`; this drives the
  // (deferred) filled-state history region and is exposed as a root modifier class for host styling.
  state: {
    type: String,
    default: "empty",
    validator: (v) => ["empty", "dirty", "filled"].includes(v),
  },
  // Uploaded reference files, passed through to ContextFiles.
  files: {
    type: Array,
    default: () => [],
  },
  // Files mid-upload, passed through to ContextFiles for the progress-bar cards.
  uploadingFiles: {
    type: Array,
    default: () => [],
  },
  // Cap shown in the ContextFiles count badge.
  maxFiles: {
    type: Number,
    default: 20,
  },
  // Stored context text, passed through to ContextEditor.
  context: {
    type: String,
    default: null,
  },
  // Heading shown above the context textarea (ER-10219 sets "Account Context" in settings).
  title: {
    type: String,
    default: "Context",
  },
  // Server-stamped last-edit metadata (ER-9891/9901). Not yet rendered —
  // reserved for the deferred filled-state history region, see ER-10314.
  contextUpdatedAt: {
    type: String,
    default: null,
  },
  contextUpdatedBy: {
    type: [String, Number],
    default: null,
  },
  // Optional unsaved draft for Storybook / hosts that need a pre-edited dirty state.
  draft: {
    type: String,
    default: null,
  },
  // Character limit for the editor. Matches the current Figma frame (/ 10,000).
  maxLength: {
    type: Number,
    default: 10000,
  },
  // ER-10218: IQ suggestion in flight / last suggestion error, passed through to ContextEditor.
  suggesting: {
    type: Boolean,
    default: false,
  },
  suggestError: {
    type: String,
    default: "",
  },
  // ER-10219: optional hover hint explaining what the IQ action will do when
  // no company websites are configured yet.
  suggestHint: {
    type: String,
    default: "",
  },
  // Disables the editor (e.g. while a save is in flight).
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["save", "discard", "suggest", "improve", "upload", "remove", "input"]);

/** ER-11347: file chosen for the reader modal (null when closed). */
const selectedFile = ref(null);
const isReaderOpen = computed(() => !!selectedFile.value);

function openFile(file) {
  selectedFile.value = file;
}

function closeReader() {
  selectedFile.value = null;
}
</script>

<style scoped>
.context-tab {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0.75rem;
}
</style>
